import { useTranslation } from "react-i18next";

import type { CcPackageSummary } from "@/api/code-companion";
import EvidenceDrawer from "@/components/agents/code-companion/EvidenceDrawer";
import GateHistory from "@/components/agents/code-companion/GateHistory";
import { useCcEvidence } from "@/hooks/useCodeCompanion";
import { CC_GOLD_INK } from "@/lib/cc-trace-tokens";

/**
 * O dossiê como destino (S-064 onda 7).
 *
 * O ledger é append-only e registra o que de fato aconteceu. No protótipo ele é uma tab
 * com o total no badge; no app vivia dentro do mesmo `Sheet` de tudo o mais, alcançável
 * só depois de clicar num card e rolar até o fim.
 *
 * Aqui embaixo do dossiê vem o histórico do gate, porque as duas perguntas se completam:
 * "o que aconteceu" e "com base em quê alguém decidiu promover".
 */
export default function EvidenceScreen({
  projectId,
  pkg,
}: {
  projectId: string;
  pkg: CcPackageSummary;
}) {
  const { t } = useTranslation();
  const { data } = useCcEvidence(projectId, pkg.id);

  return (
    <div className="flex flex-col gap-4 px-6 py-5">
      <div>
        <div
          className="mb-1 uppercase"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            color: CC_GOLD_INK,
            letterSpacing: ".2em",
            fontWeight: 600,
          }}
        >
          {t("Delivery dossier")}
        </div>
        <h2
          className="m-0 flex items-center gap-2.5"
          style={{
            fontFamily: "var(--font-brand)",
            fontWeight: 700,
            fontSize: "20px",
            letterSpacing: "-0.015em",
          }}
        >
          {pkg.devclass}
          {data && (
            <span
              data-testid="evidence-total"
              className="inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1"
              style={{
                background: "var(--exed-gold)",
                color: "var(--exed-navy)",
                fontFamily: "var(--font-mono)",
                fontSize: "9px",
                fontWeight: 700,
              }}
            >
              {data.total}
            </span>
          )}
        </h2>
        <p className="mt-1 max-w-[560px] text-[12.5px]" style={{ color: "var(--exed-ink-500)" }}>
          {t(
            "Append-only. Every promotion, artifact, deploy phase, correction attempt and transport event, with the hash of each record.",
          )}
        </p>
      </div>

      <EvidenceDrawer projectId={projectId} packageId={pkg.id} />
      <GateHistory projectId={projectId} packageId={pkg.id} />
    </div>
  );
}
